import { FC, useEffect } from "react";

import { useAuth } from './context/AuthContext';
import { BrowserRouter, Route, Routes, useLocation, Navigate } from "react-router-dom";
import AccountRequestDetails from "./components/AccountRequests/Details/AccountRequestDetails";

import './App.scss';
import AccountRequestsLayout from "./components/AccountRequests/AccountRequestsLayout";
import AppHeader from "./components/AppHeader/AppHeader";
import BatchExports from "./components/BatchExports/BatchExports";
import { useConfigureAxios } from "./useAxiosConfig";
import AuthNotification from "./components/AuthNotification/AuthNotification";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import LoginPage from "./components/LoginPage";
import { useSessionManager } from "./hooks/useSessionManager";
import SessionWarningModal from "./components/SessionWarningModal";
import { BatchExportWebSocketProvider } from "./context/BatchExportWebSocketContext";
// Component to handle routing and header
const AppContent: FC = () => {
  const location = useLocation();
  
  // Determine current view based on location
  const getCurrentView = () => {
    if (location.pathname.startsWith('/batch-exports')) {
      return 'batch-exports';
    }
    if (location.pathname.startsWith('/account-requests')) {
      return 'account-requests';
    }
    return 'account-requests'; // default
  };

  const currentView = getCurrentView();

  return (
    <>
      <div className="header-wrapper">
        <AppHeader currentView={currentView} />
      </div>
      <div className="body-wrapper">
        <Routes>
          <Route path="/" element={<Navigate to="/account-requests" replace />} />
          <Route path="/account-requests/*" element={<AccountRequestsLayout />}>
            <Route index element={<div />} />
            <Route path="details/:id" element={<AccountRequestDetails />} />
          </Route>
          <Route path="/batch-exports" element={<BatchExports />} />
        </Routes>
      </div>
    </>
  );
};

// Remember that MsalProvider must be rendered somewhere higher up in the component tree
function App() {
  const { accessToken, sessionState, isHandlingRedirect } = useAuth();
  const { showWarning, timeRemaining, continueSession } = useSessionManager(); // Initialize session management

  const AxiosConfigurator: FC = () => {
    useConfigureAxios(); // Setup Axios interceptors
    return null; // This component doesn't render anything
  };
  
  // const forceLogout = () => {
  //   // Optional: You might want to clear application-specific state before logging out
  
  //   instance.logoutPopup({
  //     postLogoutRedirectUri: "/", // Specify where to redirect after logging out, if needed
  //   }).catch(e => {
  //     console.error("Error during logout:", e);
  //   });
  
  //   // For redirect logouts, you can use:
  //   // msalInstance.logoutRedirect({ postLogoutRedirectUri: "/" });
  // };


  // useEffect(() => {
  //   const request = {
  //     // Replace "User.Read" with the scope you need for the access token
  //     scopes: ["https://springsdev.onmicrosoft.com/18f4aa28-a920-431e-9132-2bd105962975/User"],
  //     account: accounts[0],
  //     maxAge: 3600
  //   };

  //   // Fetch the access token
  //   instance.acquireTokenSilent(request).then((response) => {
  //     setAccessToken(response.accessToken);
  //     sessionStorage.setItem('accessToken', response.accessToken);
  //   }).catch((e) => {
  //     // If there is an error or the silent token acquisition fails, use popup or redirect
  //     if (e.name === 'InteractionRequiredAuthError') {
  //       instance.acquireTokenPopup(request).then((response) => {
  //         setAccessToken(response.accessToken);
  //         sessionStorage.setItem('accessToken', response.accessToken);
  //       });
  //     }
  //   });
  // }, [instance, accounts]);




  // Show loading while handling redirect
  if (isHandlingRedirect) {
    return (
      <ErrorBoundary>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}>
          <div style={{ color: 'white', fontSize: '18px' }}>Loading...</div>
        </div>
      </ErrorBoundary>
    );
  }

  // Show login page if not authenticated or session is not active
  if (!accessToken || sessionState !== 'active') {
    return (
      <ErrorBoundary>
        <LoginPage />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <BatchExportWebSocketProvider>
        <BrowserRouter>
          <div id="modal-root" className="main-wrapper">
            <AxiosConfigurator />
            <AuthNotification />
            
            {showWarning && (
              <SessionWarningModal 
                timeRemaining={timeRemaining}
                onContinue={continueSession}
              />
            )}
          
            <AppContent />
          </div>
        </BrowserRouter>
      </BatchExportWebSocketProvider>
    </ErrorBoundary>
  );
}

export default App;